import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfilesOfCurrentUser, updateProfile } from '../../redux/profilesSlice';
import ProfileCard from './ProfileCard';
import ProfileDetails from './ProfileDetails';
import CustomLoader from '../Common/CustomLoader';
import NoResults from '../Common/NoResults ';
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from '@mui/icons-material';


const PAGINATION_COLOR = '#2F4E8B';
const PER_PAGE = 6;

export default function MyProfiles() {
  const dispatch = useDispatch();
  const { currentUserProfiles, loading, error } = useSelector((s) => s.profiles);
  const [page, setPage] = useState(1);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [selectedProfile, setSelectedProfile] = useState(null);
  const containerRef = React.useRef(null);

  // Initial fetch
  useEffect(() => {
    dispatch(fetchProfilesOfCurrentUser());
  }, [dispatch]);

  // Handle update from profile list or card
  const handleUpdate = async (p) => {
    setSelectedProfile(p); // Open edit dialog for the selected profile
  };

  // Handle update inside ProfileDetails: refetch & update selectedProfile
  const handleDetailsUpdated = (updatedProfile) => {
    // on applique directement la mise à jour dans l’état local
    setSelectedProfile(updatedProfile);
    setSnack({ open: true, msg: 'Détails mis à jour !', sev: 'success' });
  };

  const handleCloseDetails = () => {
    setSelectedProfile(null);
    setPage(1);
  };

  const totalPages = Math.ceil(currentUserProfiles.length / PER_PAGE);
  const slice = currentUserProfiles.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  // Scroll to top helper
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CustomLoader />
      </Box>
    );
  }
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (currentUserProfiles.length === 0) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" height="60vh">
        <NoResults />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }} ref={containerRef}>
      {selectedProfile ? (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={handleCloseDetails}>
              <ArrowBackIosIcon sx={{ color: PAGINATION_COLOR }} />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              Détails du profil
            </Typography>
          </Box>
          <ProfileDetails
            profile={selectedProfile}
            onUpdated={handleDetailsUpdated}
          />
        </>
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <ArrowBackIosIcon
              sx={{ cursor: 'pointer', color: PAGINATION_COLOR }}
              onClick={() =>
                window.dispatchEvent(
                  new CustomEvent('navigateToComponent', {
                    detail: { component: 'dashboard' },
                  })
                )
              }
            />
            <Typography variant="h6" fontWeight={700}>
              Mes Profils
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 24,
            }}
          >
            {slice.map((p) => (
              <ProfileCard
                key={p.id}
                profile={p}
                onUpdate={handleUpdate}
                onClick={() => setSelectedProfile(p)}
              />
            ))}
          </Box>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
              <IconButton
                onClick={() => {
                  setPage((pg) => Math.max(pg - 1, 1));
                  setTimeout(scrollToTop, 0);
                }}
                disabled={page === 1}
              >
                <ArrowBackIosIcon sx={{ color: PAGINATION_COLOR }} />
              </IconButton>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: PAGINATION_COLOR,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                }}
              >
                {page}
              </Box>
              <IconButton
                onClick={() => {
                  setPage((pg) => Math.min(pg + 1, totalPages));
                  setTimeout(scrollToTop, 0);
                }}
                disabled={page === totalPages}
              >
                <ArrowForwardIosIcon sx={{ color: PAGINATION_COLOR }} />
              </IconButton>
            </Box>
          )}
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <MuiAlert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.sev}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
